// VersionsTab — agent version history + rollback + clone (B51, 2026-07-23).
//
// The backend has snapshotted every agent update into agent_versions since
// the Detail Viewer cutover (GET /versions, POST /rollback/{n}) but no UI
// ever consumed it — users saw "no version history, no rollback, no clone."
// This tab is the single home for all three.
//
// RBAC mirrors the endpoints: everyone with viewer access sees history;
// Rollback renders for administrators (endpoint enforces require_administrator);
// Clone renders for developer+ (endpoint enforces require_developer).
import React, { useEffect, useState, useCallback } from "react";
import { agentApi } from "../../../api/client";
import { COLORS } from "../../../theme/colors";

const ADMIN_ROLES = ["owner", "administrator"];
const DEV_ROLES = ["owner", "administrator", "developer"];

// Pure helper (named export for unit testing) — formats a version's
// config_snapshot into a one-line human summary. Extracted from the
// component body so it can be exercised directly; wired back below.
export function snapshotSummary(s) {
  if (!s) return "—";
  const bits = [];
  if (s.provider || s.model) bits.push(`${s.provider || "?"}/${s.model || "?"}`);
  if (s.system_prompt) bits.push(`prompt ${s.system_prompt.length} chars`);
  if (s.channels?.length) bits.push(`channels: ${s.channels.join(", ")}`);
  return bits.join(" · ") || "—";
}

export default function VersionsTab({ tree, currentUser }) {
  const agent = tree?.agent;
  const [versions, setVersions] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // version_number | "clone"
  const [notice, setNotice] = useState(null);

  const role = currentUser?.role || "";
  const canRollback = ADMIN_ROLES.includes(role);
  const canClone = DEV_ROLES.includes(role);

  const load = useCallback(async () => {
    if (!agent?.id) return;
    setError(null);
    try {
      const r = await agentApi.getVersions(agent.id);
      setVersions(r.data || []);
    } catch (e) {
      setError(e?.message || "Failed to load version history");
    }
  }, [agent?.id]);

  useEffect(() => { load(); }, [load]);

  const doRollback = async (v) => {
    if (!window.confirm(
      `Restore "${agent.name}" to version ${v.version_number}? ` +
      "The current configuration is snapshotted first, so this is reversible."
    )) return;
    setBusy(v.version_number);
    setNotice(null);
    try {
      await agentApi.rollback(agent.id, v.version_number);
      setNotice({
        kind: "ok",
        text: `Restored to version ${v.version_number}. Reopen the agent to see the restored configuration.`,
      });
      await load();
    } catch (e) {
      setNotice({ kind: "err", text: e?.message || "Rollback failed" });
    } finally {
      setBusy(null);
    }
  };

  const doClone = async () => {
    setBusy("clone");
    setNotice(null);
    try {
      const r = await agentApi.clone(agent.id);
      setNotice({
        kind: "ok",
        text: `Cloned as "${r.data?.name}" (draft). Find it in the Agent Library. ` +
              "Knowledge base and integration bindings are not copied — ingest and re-bind explicitly.",
      });
    } catch (e) {
      setNotice({ kind: "err", text: e?.message || "Clone failed" });
    } finally {
      setBusy(null);
    }
  };

  if (!agent) return null;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Version history</div>
          <div style={{ fontSize: 12, color: COLORS.textDim }}>
            A snapshot is recorded automatically on every configuration save.
          </div>
        </div>
        {canClone && (
          <button
            onClick={doClone}
            disabled={busy === "clone"}
            style={{
              background: COLORS.accent || COLORS.purple, color: "#fff",
              border: "none", borderRadius: 6, padding: "8px 14px",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
              opacity: busy === "clone" ? 0.6 : 1,
            }}
          >
            {busy === "clone" ? "Cloning…" : "Clone agent"}
          </button>
        )}
      </div>

      {notice && (
        <div style={{
          padding: "10px 14px", borderRadius: 6, marginBottom: 14, fontSize: 12.5,
          background: notice.kind === "ok" ? "rgba(21,128,61,0.12)" : "rgba(239,68,68,0.12)",
          color: notice.kind === "ok" ? (COLORS.green || "#15803d") : (COLORS.red || "#ef4444"),
        }}>
          {notice.text}
        </div>
      )}

      {error && <div style={{ color: COLORS.red || "#ef4444", fontSize: 12.5 }}>{error}</div>}
      {!error && versions === null && (
        <div style={{ color: COLORS.textDim, fontSize: 12.5 }}>Loading…</div>
      )}
      {!error && versions?.length === 0 && (
        <div style={{ color: COLORS.textDim, fontSize: 12.5 }}>
          No versions yet — the first snapshot is taken the next time this
          agent's configuration is saved.
        </div>
      )}

      {versions?.length > 0 && (
        <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: "hidden" }}>
          {versions.map((v, i) => (
            <div key={v.id} style={{
              display: "flex", alignItems: "center", gap: 14, padding: "12px 16px",
              borderTop: i === 0 ? "none" : `1px solid ${COLORS.border}`,
            }}>
              <div style={{
                fontSize: 12, fontWeight: 700, minWidth: 44,
                color: COLORS.accent || COLORS.purple,
              }}>
                v{v.version_number}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {snapshotSummary(v.config_snapshot)}
                </div>
                <div style={{ fontSize: 11, color: COLORS.textDim }}>
                  {v.created_at ? new Date(v.created_at).toLocaleString() : "—"}
                  {v.created_by ? ` · by ${v.created_by}` : ""}
                  {v.change_notes ? ` · ${v.change_notes}` : ""}
                </div>
              </div>
              {canRollback && (
                <button
                  onClick={() => doRollback(v)}
                  disabled={busy !== null}
                  style={{
                    background: "transparent",
                    border: `1px solid ${COLORS.border}`,
                    color: COLORS.text, borderRadius: 6, padding: "6px 12px",
                    fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                    opacity: busy !== null ? 0.5 : 1,
                  }}
                >
                  {busy === v.version_number ? "Restoring…" : "Rollback"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
